import React, { forwardRef, useRef } from "react";
import { mergeRefs } from "@helpers";
import { maybe } from "src/utils";
import { ValidationError } from "./date-validation.helpers";
import {
  smartInput,
  handleFieldKeyDown,
  stripNonDigits,
} from "./keyboard-input.helpers";
import { useDateInput } from "./use-date-input.hook";

type CpDatePickerInputProps = {
  name?: string;
  required?: boolean;
  minDate?: string;
  maxDate?: string;
  value?: string;
  onChange?: (value: string) => void;
  onValidityChange?: (validationErrors: ValidationError[]) => void;
  className?: string;
  disabled?: boolean;
};

const Separator = () => {
  return (
    <span className="text-[color:var(--cp-color-app-secondary-text)]">/</span>
  );
};

export const CpDatePickerInput = forwardRef<
  HTMLInputElement,
  CpDatePickerInputProps
>(
  (
    {
      name,
      required,
      minDate,
      maxDate,
      value = "",
      onChange,
      onValidityChange,
      className,
      disabled = false,
    },
    ref,
  ) => {
    const monthInputRef = useRef<HTMLInputElement>(null);
    const dayInputRef = useRef<HTMLInputElement>(null);
    const yearInputRef = useRef<HTMLInputElement>(null);

    const {
      month,
      day,
      year,
      nativeInputValue,
      setMonth,
      setDay,
      setYear,
      emitDate,
    } = useDateInput({
      value,
      required,
      minDate,
      maxDate,
      onChange,
      onValidityChange,
    });

    const handleFieldChange = (
      e: React.ChangeEvent<HTMLInputElement>,
      field: "month" | "day",
      currentValue: string,
      setDateField: (value: string) => void,
    ) => {
      const numbersOnly = stripNonDigits(e.target.value);
      if (numbersOnly.length === 0) {
        setDateField("");
        emitDate(
          field === "month" ? "" : month,
          field === "day" ? "" : day,
          year,
        );
        return;
      }

      const cleanCurrentValue = stripNonDigits(currentValue);
      if (numbersOnly.length > cleanCurrentValue.length) {
        const newDigit = numbersOnly.slice(-1);
        const newValue = smartInput(currentValue, newDigit, field);
        setDateField(newValue);
        emitDate(
          field === "month" ? newValue : month,
          field === "day" ? newValue : day,
          year,
        );

        // Auto-focus to next field if value is complete (2 digits)
        if (newValue.length === 2) {
          if (field === "month") {
            dayInputRef.current?.focus();
          } else if (field === "day") {
            yearInputRef.current?.focus();
          }
        }
      } else if (numbersOnly !== cleanCurrentValue) {
        const newValue = numbersOnly.slice(0, 2);
        setDateField(newValue);
        emitDate(
          field === "month" ? newValue : month,
          field === "day" ? newValue : day,
          year,
        );
      }
    };

    const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const numbersOnly = stripNonDigits(e.target.value).slice(0, 4);
      setYear(numbersOnly);
      emitDate(month, day, numbersOnly);
    };

    const handleKeyDown = (
      e: React.KeyboardEvent<HTMLInputElement>,
      field: "month" | "day" | "year",
    ) => {
      handleFieldKeyDown(e, {
        currentField: field,
        refs: { monthInputRef, dayInputRef, yearInputRef },
        values: { month, day, year },
        setters: { setMonth, setDay, setYear },
        onValueChange: (newMonth, newDay, newYear) => {
          emitDate(newMonth, newDay, newYear);
        },
      });
    };

    const handleFieldBlur = (
      e: React.FocusEvent<HTMLInputElement>,
      setFieldValue: (value: string) => void,
    ) => {
      const inputValue = e.target.value;
      // Add leading zero to single digit values
      if (inputValue && inputValue.length === 1 && /^\d$/.test(inputValue)) {
        const paddedValue = inputValue.padStart(2, "0");
        setFieldValue(paddedValue);
      }
      handleBlur();
    };

    const handleBlur = () => {
      if (month && day && year) {
        emitDate(month, day, year);
      }
    };

    const inputClasses = `text-center bg-transparent rounded px-0 py-1 transition-colors border-0 ${
      disabled
        ? "text-[color:var(--cp-color-app-disabled-text)] cursor-not-allowed"
        : ""
    }`;

    return (
      <div className={`relative ${className || ""}`}>
        <div className="flex items-center">
          <input
            // Passing the forwardedRef here so that react-hook-form can focus the input when there's validation errors
            ref={mergeRefs(monthInputRef, ref)}
            type="text"
            placeholder="MM"
            value={month}
            aria-label={name + " month"}
            inputMode="numeric"
            tabIndex={0}
            disabled={disabled}
            onChange={(e) => handleFieldChange(e, "month", month, setMonth)}
            onKeyDown={(e) => handleKeyDown(e, "month")}
            onBlur={(e) => handleFieldBlur(e, setMonth)}
            className={`w-6 ${inputClasses} ${maybe(!month, "mr-0.5") /** MM is too large to align with the separator, but regular digits look fine so if we're using the placeholder add some space */}`}
          />
          <Separator />
          <input
            ref={dayInputRef}
            type="text"
            placeholder="DD"
            aria-label={name + " day"}
            value={day}
            inputMode="numeric"
            // Only allowing month to be tabbed to, arrow keys are to be used to navigate between the fields. This follows the mui date picker behavior
            tabIndex={-1}
            disabled={disabled}
            onChange={(e) => handleFieldChange(e, "day", day, setDay)}
            onKeyDown={(e) => handleKeyDown(e, "day")}
            onBlur={(e) => handleFieldBlur(e, setDay)}
            className={`w-6 ${inputClasses} -mx-px`}
          />
          <Separator />
          <input
            ref={yearInputRef}
            type="text"
            placeholder="YYYY"
            aria-label={name + " year"}
            value={year}
            inputMode="numeric"
            tabIndex={-1}
            disabled={disabled}
            onChange={handleYearChange}
            onKeyDown={(e) => handleKeyDown(e, "year")}
            onBlur={handleBlur}
            className={`w-10 ${inputClasses} -ml-px`}
          />
        </div>
        <input
          type="date"
          name={name}
          value={nativeInputValue}
          required={required}
          min={minDate}
          max={maxDate}
          disabled={disabled}
          className="sr-only pointer-events-none"
          tabIndex={-1}
          onChange={() => {
            // Providing empty onChange to prevent console/test warnings. We're controlling this value through the above inputs.
          }}
        />
      </div>
    );
  },
);
